import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./ResponsableInformation.module.sass";
import Card from "../../../../components/Card";
import TextInput from "../../../../components/TextInput";
import { searchProspectsForOffer } from "../../../../services/student.service";
import { addProspectToOffer } from "../../../../services/offerAdmin.service";
import LetterAvatar from "../../../../components/LetterAvatar";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

const ProspectInformation = ({ className, onSelectProspect, onProspectAdded }) => {
  const { id: offerId } = useParams(); // Récupérer l'ID de l'offre depuis l'URL
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingProspect, setIsAddingProspect] = useState(false);



  // Charger tous les prospects pertinents pour les offres (statuts: Nouveau, En cours, Recherche entreprise)
  const [allProspects, setAllProspects] = useState([]);

  useEffect(() => {
    const loadAllProspects = async () => {
      setIsLoading(true);
      try {
        // Récupérer tous les prospects avec les bons statuts (sans requête de recherche)
        const results = await searchProspectsForOffer('');
        console.log('Prospects chargés (Nouveau, En cours, Recherche entreprise):', results?.length || 0, results);
        setAllProspects(results || []);
      } catch (error) {
        console.error('Erreur lors du chargement des prospects:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllProspects();
  }, []);

  // Effectuer la recherche avec la fonction optimisée
  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.length >= 2) {
        setIsLoading(true);
        try {
          // Utiliser searchProspectsForOffer avec la requête de recherche
          const results = await searchProspectsForOffer(searchQuery);
          console.log('Résultats de recherche:', results?.length || 0, results);
          setSearchResults(results || []);
          setShowResults(true);
        } catch (error) {
          console.error('Erreur lors de la recherche:', error);
          setSearchResults([]);
          setShowResults(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Si pas de recherche ou moins de 2 caractères, ne rien afficher
        setSearchResults([]);
        setShowResults(false);
      }
    };

    // Debounce la recherche pour éviter trop d'appels API
    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, allProspects]);

  // Gérer la sélection d'un prospect
  const handleSelectProspect = (prospect) => {
    setSelectedProspect(prospect);
    // Utiliser first_name et last_name pour construire le nom complet
    const fullName = `${prospect.first_name || ''} ${prospect.last_name || ''}`.trim();
    setSearchQuery(fullName);
    setShowResults(false);

    if (onSelectProspect) {
      onSelectProspect(prospect);
    }
  };

  // Gérer le changement dans la barre de recherche
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // Vérifier si la valeur de recherche correspond au prospect sélectionné
    if (selectedProspect) {
      const fullName = `${selectedProspect.first_name || ''} ${selectedProspect.last_name || ''}`.trim();
      if (e.target.value !== fullName) {
        setSelectedProspect(null);
      }
    }
  };

  return (
    <Card
      className={cn(styles.card, className)}
      title="Élève(e)"
      classTitle="title-red"
    >
      <div className={styles.fieldset}>
        <div className={styles.searchContainer}>
          <TextInput
            className={styles.field}
            label="Rechercher un prospect"
            name="prospect-search"
            type="text"
            placeholder="Nom, email ou téléphone du prospect (min. 2 caractères)"
            value={searchQuery}
            onChange={handleSearchChange}
          />

          {isLoading && (
            <div className={styles.loading}>Recherche en cours...</div>
          )}

          {showResults && searchResults.length > 0 && (
            <div className={styles.searchResults}>
              {searchResults.map((prospect, index) => (
                <div
                  key={prospect.id || index}
                  className={styles.resultItem}
                  onClick={() => handleSelectProspect(prospect)}
                >
                  <div className={styles.resultContent}>
                    <div style={{ marginRight: '20px' }}>
                      <LetterAvatar
                        firstName={prospect.first_name}
                        lastName={prospect.last_name}
                        size={40}
                      />
                    </div>
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>{`${prospect.first_name || ''} ${prospect.last_name || ''}`.trim() || 'Sans nom'}</div>
                      <div className={styles.userEmail}>{prospect.email || 'Email non disponible'} • {prospect.phone || 'Téléphone non disponible'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !isLoading && !selectedProspect && (
            <div className={styles.noResults}>
              Aucun prospect trouvé avec consentement RGPD et statuts "Nouveau", "En cours" ou "Recherche entreprise"
            </div>
          )}
          
          {searchQuery.length === 0 && allProspects.length === 0 && !isLoading && (
            <div className={styles.noResults}>
              Aucun prospect disponible avec consentement RGPD pour les offres d'alternance
            </div>
          )}
        </div>

        {selectedProspect && (
          <div className={styles.selectedUserDetails}>
            <h3>Prospect sélectionné</h3>

            <div className={styles.detailRow}>
              <span className={styles.label}>Nom:</span>
              <span className={styles.value}>{`${selectedProspect.first_name || ''} ${selectedProspect.last_name || ''}`.trim() || 'Non renseigné'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Email:</span>
              <span className={styles.value}>{selectedProspect.email || 'Email non disponible'}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.label}>Téléphone:</span>
              <span className={styles.value}>{selectedProspect.phone || 'Téléphone non disponible'}</span>
            </div>
          </div>
        )}

        {selectedProspect && (
          <div className={styles.actions}>
            <button
              className={cn("button", "button-stroke", styles.button)}
              type="button"
              disabled={isAddingProspect}
              onClick={async () => {
                if (!offerId) {
                  toast.error('ID de l\'offre manquant');
                  return;
                }
                
                setIsAddingProspect(true);
                try {
                  const result = await addProspectToOffer(offerId, selectedProspect.id);
                  
                  if (result.success) {
                    toast.success('Prospect partagé avec succès!');
                    
                    setSelectedProspect(null);
                    setSearchQuery('');
                    
                    if (onSelectProspect) {
                      onSelectProspect(null);
                    }
                    // Déclencher la mise à jour de la liste des candidats
                    if (onProspectAdded) {
                      onProspectAdded();
                    }
                  } else {
                    toast.error(result.error || 'Erreur lors de l\'ajout du prospect');
                  }
                } catch (error) {
                  console.error('Erreur lors de l\'ajout du prospect:', error);
                  toast.error('Erreur lors de l\'ajout du prospect');
                } finally {
                  setIsAddingProspect(false);
                }
              }}
            >
              {isAddingProspect ? 'Partage en cours...' : 'Partager à l\'entreprise'}
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ProspectInformation;
